import React, { useState } from 'react';
import '../../css/Login.css';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axios';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeBrand, setActiveBrand] = useState('xpanse'); // 'xpanse' or 'namaste'
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);

    try {
      const response = await axios.post("admin-users/signin", formData);

      if (response?.data?.status === true) {
        toast.success(response.data.data.message, {
          position: "top-center",
          autoClose: 3000,
          theme: "light",
        });
      }
      console.log("Login Response :", response?.data);
      const authToken = response?.data?.data?.jwt;
      navigate("/enquiry-listing");
      localStorage.setItem('authToken', authToken)
      localStorage.setItem('email', response?.data?.data?.user?.email)
    } catch (error) {
      console.log("Error While Signing in :", error);

      if (error.response && error.response?.data?.status === false && error.response?.status === 406) {
        let errorMessage = error?.response?.data?.error;

        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
          theme: "light",
        });
      } else if (error.response && error.response?.data?.status === false && error.response?.status === 400) {
        let errorMessage = error?.response?.data?.data?.title;
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
          theme: "light",
        });
      } else if (error.response && error.response?.data?.status === false && error.response?.status === 412) {
        let errorMessage = error?.response?.data?.data?.message;
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
          theme: "light",
        });
      } else if (error.response && error.response?.data?.status === false && error.response?.status === 404) {
        let errorMessage = error?.response?.data?.data?.message;
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
          theme: "light",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getBrandColors = () => {
    return activeBrand === 'xpanse' ? '#0a4f7a' : '#ff7044';
  };

  return (
    <div className={`login-container ${activeBrand}`}>
      <div className="login-header">
        <div className="brand-logos">
          <img
            className={`login-logo xpanse-logo ${activeBrand === 'xpanse' ? 'active' : ''}`}
            src={require('../../assets/xpa-logo.png')}
            alt="Xpanse Logo"
            onClick={() => setActiveBrand('xpanse')}
          />
          <div className="logo-divider"></div>
          <img
            className={`login-logo namaste-logo ${activeBrand === 'namaste' ? 'active' : ''}`}
            src={require('../../assets/nam-logo.png')}
            alt="Namaste Logo"
            onClick={() => setActiveBrand('namaste')}
          />
        </div>
      </div>

      <div className="login-form">
        <h2 className='fw-bold' style={{ color: getBrandColors() }}>
          <i>Welcome Back</i>
        </h2>
        <p className="login-subtitle">Please sign in to your account</p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="Enter your email"
              style={{
                borderColor: errors.email ? '#e74c3c' : '',
                '--focus-color': getBrandColors()
              }}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group password-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'error' : ''}
                placeholder="Enter your password"
                style={{
                  borderColor: errors.password ? '#e74c3c' : '',
                  '--focus-color': getBrandColors()
                }}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                style={{ color: getBrandColors() }}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
            style={{
              background: `linear-gradient(135deg, ${getBrandColors()} 0%, ${getBrandColors()}dd 100%)`
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;